import React, { useMemo } from 'react';
import {
  SimpleGrid,
  Card,
  CardBody,
  Flex,
  Box,
  Text,
  Heading,
  Icon,
  VStack,
} from '@chakra-ui/react';
import {
  FaClock,
  FaCheckCircle,
  FaVial,
  FaBuilding,
  FaFileSignature,
} from 'react-icons/fa';

const DoctorAnalyticsCards = ({ submissions, testTypeNames, doctorSession }) => {
  const analytics = useMemo(() => {
    const total = submissions.length;
    const completed = submissions.filter((s) => s.status === 'completed');
    const completionRate = total > 0 ? ((completed.length / total) * 100).toFixed(1) : '0.0';

    // Average turnaround time (submitted_at → updated_at for completed)
    const withDates = completed.filter((s) => s.submitted_at && s.updated_at);
    let avgTurnaround = null;
    if (withDates.length > 0) {
      const totalHours = withDates.reduce((sum, s) => {
        return sum + (new Date(s.updated_at) - new Date(s.submitted_at)) / (1000 * 60 * 60);
      }, 0);
      const avgDays = totalHours / withDates.length / 24;
      avgTurnaround = avgDays.toFixed(1);
    }

    // Pending authorizations (has lab_results_url but not physician_approved)
    const pendingAuth = submissions.filter(
      (s) => s.lab_results_url && !s.physician_approved
    ).length;

    // Test type breakdown (top 5)
    const testCounts = {};
    submissions.forEach((s) => {
      if (s.test_type_id) {
        const name = testTypeNames[s.test_type_id] || s.test_type_id;
        testCounts[name] = (testCounts[name] || 0) + 1;
      }
    });
    const topTests = Object.entries(testCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Client name breakdown (top 5)
    const clientCounts = {};
    submissions.forEach((s) => {
      const name = s.client_name || doctorSession?.clinic_name || 'Unassigned';
      clientCounts[name] = (clientCounts[name] || 0) + 1;
    });
    const topClients = Object.entries(clientCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return { completionRate, avgTurnaround, pendingAuth, topTests, topClients };
  }, [submissions, testTypeNames, doctorSession?.clinic_name]);

  const cards = [
    {
      label: 'Avg Turnaround',
      value: analytics.avgTurnaround ? `${analytics.avgTurnaround}d` : 'N/A',
      subtitle: analytics.avgTurnaround ? 'days to completion' : 'No completed data',
      icon: FaClock,
      color: 'blue',
    },
    {
      label: 'Completion Rate',
      value: `${analytics.completionRate}%`,
      subtitle: `${submissions.filter((s) => s.status === 'completed').length} of ${submissions.length}`,
      icon: FaCheckCircle,
      color: 'green',
    },
    {
      label: 'Pending Approval',
      value: analytics.pendingAuth,
      subtitle: 'results awaiting signature',
      icon: FaFileSignature,
      color: 'orange',
    },
    {
      label: 'Top Test Type',
      value: analytics.topTests.length > 0 ? analytics.topTests[0][1] : '0',
      subtitle: analytics.topTests.length > 0 ? analytics.topTests[0][0] : 'No tests yet',
      icon: FaVial,
      color: 'purple',
    },
    {
      label: 'Top Client',
      value: analytics.topClients.length > 0 ? analytics.topClients[0][1] : '0',
      subtitle: analytics.topClients.length > 0 ? analytics.topClients[0][0] : 'No clients yet',
      icon: FaBuilding,
      color: 'teal',
    },
  ];

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing={4}>
      {cards.map((card) => (
        <Card key={card.label} bg="white" borderWidth="1px" borderColor="gray.200" shadow="sm">
          <CardBody py={4} px={4}>
            <Flex justify="space-between" align="flex-start">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  {card.label}
                </Text>
                <Heading size="lg" fontWeight="700">
                  {card.value}
                </Heading>
                <Text fontSize="xs" color="gray.400" noOfLines={1}>
                  {card.subtitle}
                </Text>
              </VStack>
              <Box
                bg={`${card.color}.50`}
                color={`${card.color}.500`}
                p={2.5}
                borderRadius="xl"
              >
                <Icon as={card.icon} boxSize={4} />
              </Box>
            </Flex>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );
};

export default DoctorAnalyticsCards;
